import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Loader2, Wallet, Users, Eye, Hand, CalendarDays, List, ChevronDown, HardHat } from "lucide-react";
import { toast } from "sonner";
import { PayoutAdjustments } from "./PayoutAdjustments";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const parseDate = (iso) => (iso ? new Date(`${iso}T00:00:00`) : null);

const weekStartOf = (iso) => {
  const d = parseDate(iso);
  if (!d) return "";
  const offset = (d.getDay() + 6) % 7; // Monday = 0
  d.setDate(d.getDate() - offset);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const weekLabel = (iso) => {
  const s = parseDate(iso);
  const e = parseDate(iso);
  e.setDate(e.getDate() + 6);
  return `${s.getDate()} ${MONTHS[s.getMonth()]} – ${e.getDate()} ${MONTHS[e.getMonth()]}`;
};

const dayLabel = (iso) => {
  const d = parseDate(iso);
  if (!d) return "—";
  return `${DAYS[d.getDay()]}, ${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
};

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// Sum each mover's hours + pay across a set of jobs.
const moverTotals = (jobList) => {
  const m = new Map();
  (jobList || []).forEach((j) => {
    (j.movers || []).forEach((mv) => {
      const cur = m.get(mv.mover) || { mover: mv.mover, hours: 0, pay: 0, jobs: [] };
      cur.hours += Number(mv.hours) || 0;
      cur.pay += Number(mv.pay) || 0;
      cur.jobs.push({ job_id: j.job_id, date: j.date, hours: r2(mv.hours), pay: r2(mv.pay), source: j.source });
      m.set(mv.mover, cur);
    });
  });
  return Array.from(m.values())
    .map((x) => ({ ...x, hours: r2(x.hours), pay: r2(x.pay) }))
    .sort((a, b) => b.pay - a.pay);
};

export const MyEarnings = ({ onView }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("jobs");
  const [grain, setGrain] = useState("week");
  const [moverGrain, setMoverGrain] = useState("week");
  const [openCrew, setOpenCrew] = useState({});
  const [openMover, setOpenMover] = useState({});
  const [openShare, setOpenShare] = useState({});
  const [openCrewMover, setOpenCrewMover] = useState({});
  const [bulking, setBulking] = useState(false);

  const toggleCrew = (key) => setOpenCrew((p) => ({ ...p, [key]: !p[key] }));

  const markMoverPaid = async (weekKey, mover, paid) => {
    try {
      await axios.put(`${API}/payments/status`, { date: weekKey, mover, paid });
      toast.success(paid ? `${mover} marked paid` : `${mover} marked unpaid`);
      load();
    } catch {
      toast.error("Could not update payment status");
    }
  };

  const markWeekPaid = async (weekKey, movers, paid) => {
    try {
      await Promise.all((movers || []).map((m) => axios.put(`${API}/payments/status`, { date: weekKey, mover: m.mover, paid })));
      toast.success(paid ? "All movers marked paid" : "All movers marked unpaid");
      load();
    } catch {
      toast.error("Could not update payment status");
    }
  };

  const markEarlierWeeksPaid = async (allGroups) => {
    const earlier = (allGroups || []).slice(1); // keep the latest (top) period, mark everything before it
    if (earlier.length === 0) {
      toast.info("No earlier periods to mark.");
      return;
    }
    if (!window.confirm("Mark every period before the latest one as paid? You can still change any period individually afterwards.")) return;
    setBulking(true);
    try {
      const puts = [];
      earlier.forEach((w) => w.movers.forEach((m) => puts.push(axios.put(`${API}/payments/status`, { date: w.weekKey, mover: m.mover, paid: true }))));
      await Promise.all(puts);
      toast.success("Earlier periods marked paid.");
      load();
    } catch {
      toast.error("Could not mark earlier periods paid");
    } finally {
      setBulking(false);
    }
  };

  const load = () => {
    setLoading(true);
    axios
      .get(`${API}/contractor/earnings`)
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Could not load your earnings"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const jobs = data?.jobs || [];

  const moverAdjIndex = useMemo(() => {
    const idx = {};
    (data?.mover_adjustments || []).forEach((a) => {
      const k = `${a.week_start}|${a.payee_name}`;
      (idx[k] = idx[k] || []).push(a);
    });
    return idx;
  }, [data]);
  const shareAdjIndex = useMemo(() => {
    const idx = {};
    (data?.share_adjustments || []).forEach((a) => {
      (idx[a.week_start] = idx[a.week_start] || []).push(a);
    });
    return idx;
  }, [data]);

  const groups = useMemo(() => {
    const applyAdj = grain === "week";
    const map = new Map();
    jobs.forEach((j) => {
      if (!j.date) return;
      const key = grain === "week" ? weekStartOf(j.date) : j.date;
      const g = map.get(key) || { key, share: 0, crew: 0, count: 0, jobs: [] };
      g.share += Number(j.share_amount) || 0;
      g.crew += Number(j.mover_payout) || 0;
      g.count += 1;
      g.jobs.push(j);
      map.set(key, g);
    });
    return Array.from(map.values())
      .map((g) => {
        const wk = weekStartOf(g.key);
        let movers = moverTotals(g.jobs).map((m) => {
          const adjNet = applyAdj ? r2((moverAdjIndex[`${wk}|${m.mover}`] || []).reduce((s, a) => s + (Number(a.signed) || 0), 0)) : 0;
          return { ...m, adjNet, payWithAdj: r2(m.pay + adjNet) };
        });
        const crewAdj = r2(movers.reduce((s, m) => s + (m.adjNet || 0), 0));
        const shareAdj = applyAdj ? r2((shareAdjIndex[wk] || []).reduce((s, a) => s + (Number(a.signed) || 0), 0)) : 0;
        const share = r2(g.share + shareAdj);
        const crew = r2(g.crew + crewAdj);
        return { ...g, weekKey: wk, baseShare: r2(g.share), shareAdj, shareAdjItems: applyAdj ? shareAdjIndex[wk] || [] : [], crewAdj, share, crew, net: r2(share - crew), movers };
      })
      .sort((a, b) => (a.key < b.key ? 1 : -1));
  }, [jobs, grain, moverAdjIndex, shareAdjIndex]);

  // Movers Pay tab — weekly or daily, each period with per-mover totals + paid status + job detail.
  const moverGroups = useMemo(() => {
    const payStatus = data?.mover_pay_status || {};
    const applyAdj = moverGrain === "week";
    const map = new Map();
    jobs.forEach((j) => {
      if (!j.date) return;
      const key = moverGrain === "week" ? weekStartOf(j.date) : j.date;
      const g = map.get(key) || { key, jobs: [] };
      g.jobs.push(j);
      map.set(key, g);
    });
    return Array.from(map.values())
      .map((g) => {
        const wk = weekStartOf(g.key);
        const mm = new Map();
        g.jobs.forEach((j) => {
          (j.movers || []).forEach((mv) => {
            const cur = mm.get(mv.mover) || { mover: mv.mover, hours: 0, pay: 0, jobs: [] };
            cur.hours += Number(mv.hours) || 0;
            cur.pay += Number(mv.pay) || 0;
            cur.jobs.push({ job_id: j.job_id, date: j.date, hours: r2(mv.hours), travel: r2(mv.travel || 0), pay: r2(mv.pay), extra: r2(mv.extra || 0), extraNote: mv.extra_note || "", customer: j.customer, source: j.source, jobShare: Number(j.share_amount) || 0 });
            mm.set(mv.mover, cur);
          });
        });
        const movers = Array.from(mm.values())
          .map((m) => {
            const st = payStatus[`${wk}|${m.mover}`] || {};
            const adj = applyAdj ? moverAdjIndex[`${wk}|${m.mover}`] || [] : [];
            const adjNet = r2(adj.reduce((s, a) => s + (Number(a.signed) || 0), 0));
            const jobExtra = r2((m.jobs || []).reduce((s, jb) => s + (Number(jb.extra) || 0), 0));
            return { ...m, hours: r2(m.hours), pay: r2(m.pay), adj, adjNet, jobExtra, extrasEarned: r2(jobExtra + adjNet), payWithAdj: r2(m.pay + adjNet), paid: !!st.paid, paid_at: st.paid_at || "" };
          })
          .sort((a, b) => b.payWithAdj - a.payWithAdj);
        return { key: g.key, weekKey: wk, movers, total: r2(movers.reduce((s, m) => s + m.payWithAdj, 0)), allPaid: movers.length > 0 && movers.every((m) => m.paid) };
      })
      .filter((g) => g.movers.length > 0)
      .sort((a, b) => (a.key < b.key ? 1 : -1));
  }, [jobs, moverGrain, data, moverAdjIndex]);


  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="earnings-loading">
        <Loader2 className="h-6 w-6 animate-spin text-blaze" />
      </div>
    );
  }

  return (
    <div className="mt-5 space-y-5" data-testid="my-earnings">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="rounded-2xl border border-line bg-white p-5 shadow-sm" data-testid="earnings-total-share">
          <p className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-blaze">
            <Wallet className="h-4 w-4" /> My Earnings
          </p>
          <p className="mt-2 text-3xl font-black text-slate-900">{money(data?.total_share)}</p>
          <p className="mt-1 text-xs text-slate-500">Your share from completed jobs</p>
        </div>
        <div className="rounded-2xl border border-line bg-white p-5 shadow-sm" data-testid="earnings-total-payout">
          <p className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
            <Users className="h-4 w-4" /> Crew Pay
          </p>
          <p className="mt-2 text-3xl font-black text-slate-900">{money(data?.total_mover_payout)}</p>
          <p className="mt-1 text-xs text-slate-500">What you pay your movers (hours × rate)</p>
        </div>
        <div className="rounded-2xl border border-line bg-white p-5 shadow-sm" data-testid="earnings-job-count">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Completed Jobs</p>
          <p className="mt-2 text-3xl font-black text-slate-900">{data?.job_count || 0}</p>
          <p className="mt-1 text-xs text-slate-500">MoversNearby share {data?.default_share_percent ?? 70}%</p>
        </div>
      </div>

      <div className="flex gap-1 rounded-xl border border-line bg-slate-100/70 p-1" data-testid="earnings-tabs">
        <button
          onClick={() => setTab("jobs")}
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${tab === "jobs" ? "bg-white text-blaze shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
          data-testid="earnings-tab-jobs"
        >
          <List className="h-3.5 w-3.5" /> Jobs
        </button>
        <button
          onClick={() => setTab("breakdown")}
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${tab === "breakdown" ? "bg-white text-blaze shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
          data-testid="earnings-tab-breakdown"
        >
          <CalendarDays className="h-3.5 w-3.5" /> Breakdown
        </button>
        <button
          onClick={() => setTab("movers")}
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${tab === "movers" ? "bg-white text-blaze shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
          data-testid="earnings-tab-movers"
        >
          <HardHat className="h-3.5 w-3.5" /> Movers Pay
        </button>
      </div>

      {tab === "jobs" ? (
        jobs.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-line bg-slate-50/50 py-16 text-center text-sm text-slate-500" data-testid="earnings-empty">
            No completed jobs yet. Your earnings appear here once a job is finished.
          </div>
        ) : (
          <div className="space-y-3">
            {jobs.map((j) => (
              <div key={j.job_id} className="rounded-2xl border border-line bg-white p-4 shadow-sm" data-testid={`earnings-job-${j.job_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-bold text-slate-900">{j.customer || "Customer"}</p>
                    <p className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                      <span>{j.job_id} · {j.date || "—"}</span>
                      <span
                        className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${j.source === "movers_buddy" ? "bg-amber-100 text-amber-700" : "bg-blaze/10 text-blaze"}`}
                        data-testid={`earnings-source-${j.job_id}`}
                      >
                        {j.source === "movers_buddy" ? "Movers Buddy" : "MoversNearby"}
                      </span>
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-blaze">Your share ({j.share_percent}%)</p>
                    <p className="text-xl font-black text-slate-900" data-testid={`earnings-share-${j.job_id}`}>{money(j.share_amount)}</p>
                  </div>
                </div>

                <div className="mt-3 rounded-xl bg-slate-50 p-3">
                  <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">Crew Pay</p>
                  {(j.movers || []).length === 0 ? (
                    <p className="text-xs text-slate-400">No movers logged on this job.</p>
                  ) : (
                    <div className="space-y-1">
                      {j.movers.map((m, i) => (
                        <div key={i} className="flex items-center justify-between text-xs text-slate-700" data-testid={`earnings-mover-${j.job_id}-${i}`}>
                          <span>{m.mover} · {m.hours}h × ${m.rate}/hr</span>
                          <span className="font-semibold">{money(m.pay)}</span>
                        </div>
                      ))}
                      <div className="flex items-center justify-between border-t border-line pt-1 text-xs font-bold text-slate-900">
                        <span>Total crew pay</span>
                        <span>{money(j.mover_payout)}</span>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                  <span>{(j.extras_total || 0) > 0 ? `Includes ${money(j.extras_total)} extras` : ""}</span>
                  <button
                    onClick={() => onView?.(j.job_id)}
                    className="inline-flex items-center gap-1 font-semibold text-blaze hover:underline"
                    data-testid={`earnings-view-${j.job_id}`}
                  >
                    <Eye className="h-3.5 w-3.5" /> View
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      ) : tab === "breakdown" ? (
        <div className="space-y-3" data-testid="earnings-breakdown">
          <div className="flex gap-1 rounded-xl border border-line bg-white p-1 sm:w-64">
            {[["week", "Weekly"], ["day", "Daily"]].map(([g, label]) => (
              <button
                key={g}
                onClick={() => setGrain(g)}
                className={`flex-1 rounded-lg px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${grain === g ? "bg-blaze text-white" : "text-slate-500 hover:text-slate-700"}`}
                data-testid={`breakdown-grain-${g}`}
              >
                {label}
              </button>
            ))}
          </div>

          {groups.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-line bg-slate-50/50 py-16 text-center text-sm text-slate-500" data-testid="breakdown-empty">
              No completed jobs yet.
            </div>
          ) : (
            <div className="space-y-2.5">
              {groups.map((g) => (
                <div key={g.key} className="rounded-2xl border border-line bg-white p-4 shadow-sm" data-testid={`breakdown-row-${g.key}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-bold text-slate-900">{grain === "week" ? weekLabel(g.key) : dayLabel(g.key)}</p>
                    <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-[10px] font-bold text-slate-600" data-testid={`breakdown-count-${g.key}`}>
                      {g.count} job{g.count === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                    <button
                      type="button"
                      onClick={() => setOpenShare((p) => ({ ...p, [g.key]: !p[g.key] }))}
                      className="rounded-xl bg-blaze/[0.06] px-2 py-2.5 text-center transition-colors hover:bg-blaze/[0.12]"
                      data-testid={`breakdown-share-toggle-${g.key}`}
                    >
                      <p className="flex items-center justify-center gap-1 text-[9px] font-bold uppercase tracking-widest text-blaze">
                        Your Share <ChevronDown className={`h-3 w-3 transition-transform ${openShare[g.key] ? "rotate-180" : ""}`} />
                      </p>
                      <p className="mt-1 text-base font-black text-slate-900" data-testid={`breakdown-share-${g.key}`}>{money(g.share)}</p>
                    </button>
                    <button
                      type="button"
                      onClick={() => toggleCrew(g.key)}
                      className="rounded-xl bg-slate-50 px-2 py-2.5 text-center transition-colors hover:bg-slate-100"
                      data-testid={`breakdown-crew-toggle-${g.key}`}
                    >
                      <p className="flex items-center justify-center gap-1 text-[9px] font-bold uppercase tracking-widest text-slate-500">
                        Crew Pay <ChevronDown className={`h-3 w-3 transition-transform ${openCrew[g.key] ? "rotate-180" : ""}`} />
                      </p>
                      <p className="mt-1 text-base font-black text-slate-900" data-testid={`breakdown-crew-${g.key}`}>{money(g.crew)}</p>
                    </button>
                    <div className={`rounded-xl px-2 py-2.5 ${g.net >= 0 ? "bg-green-50" : "bg-red-50"}`}>
                      <p className={`text-[9px] font-bold uppercase tracking-widest ${g.net >= 0 ? "text-green-700" : "text-red-600"}`}>Net</p>
                      <p className={`mt-1 text-base font-black ${g.net >= 0 ? "text-green-700" : "text-red-600"}`} data-testid={`breakdown-net-${g.key}`}>{money(g.net)}</p>
                    </div>
                  </div>
                  {openShare[g.key] && (
                    <div className="mt-2 rounded-xl bg-blaze/[0.05] p-3" data-testid={`breakdown-share-detail-${g.key}`}>
                      <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-blaze">Your share per job</p>
                      <div className="space-y-1.5">
                        {g.jobs.map((j) => (
                          <div key={j.job_id} className="flex items-center justify-between gap-2 text-xs text-slate-700" data-testid={`breakdown-share-job-${g.key}-${j.job_id}`}>
                            <span className="flex min-w-0 items-center gap-1.5 truncate">
                              <span className="truncate">{j.job_id} <span className="text-slate-400">· {j.date}</span></span>
                              <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase ${j.source === "movers_buddy" ? "bg-amber-100 text-amber-700" : "bg-blaze/10 text-blaze"}`}>
                                {j.source === "movers_buddy" ? "Buddy" : "MN"}
                              </span>
                              <span className="shrink-0 text-[10px] text-slate-400">{j.share_percent}%</span>
                            </span>
                            <span className="shrink-0 font-bold text-slate-900">{money(j.share_amount)}</span>
                          </div>
                        ))}
                        {g.shareAdjItems.map((a) => (
                          <div key={a.id} className="flex items-center justify-between gap-2 text-xs" data-testid={`breakdown-shareadj-${g.key}-${a.id}`}>
                            <span className="flex min-w-0 items-center gap-1.5 truncate">
                              <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase ${a.kind === "addon" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-600"}`}>{a.kind === "addon" ? "Add-on" : "Deduct"}</span>
                              <span className="truncate text-slate-500">{a.reason || "Admin adjustment"}</span>
                            </span>
                            <span className={`shrink-0 font-bold ${a.kind === "addon" ? "text-green-700" : "text-red-600"}`}>{a.kind === "addon" ? "+" : "−"}{money(a.amount)}</span>
                          </div>
                        ))}
                        <div className="flex items-center justify-between border-t border-blaze/20 pt-1 text-xs font-bold text-blaze">
                          <span>Total your share</span>
                          <span>{money(g.share)}</span>
                        </div>
                      </div>
                    </div>
                  )}
                  {openCrew[g.key] && (
                    <div className="mt-2 rounded-xl bg-slate-50 p-3" data-testid={`breakdown-crew-detail-${g.key}`}>
                      <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-slate-500">Who got paid what</p>
                      {g.movers.length === 0 ? (
                        <p className="text-xs text-slate-400">No movers logged for this period.</p>
                      ) : (
                        <div className="space-y-1">
                          {g.movers.map((m, i) => {
                            const mkey = `${g.key}-${i}`;
                            const mOpen = !!openCrewMover[mkey];
                            return (
                              <div key={i} className="rounded-lg bg-white/70">
                                <button
                                  type="button"
                                  onClick={() => setOpenCrewMover((p) => ({ ...p, [mkey]: !p[mkey] }))}
                                  className="flex w-full items-center justify-between gap-2 px-2 py-1.5 text-left text-xs text-slate-700"
                                  data-testid={`breakdown-crew-mover-${g.key}-${i}`}
                                >
                                  <span className="flex items-center gap-1 font-medium">
                                    {m.mover} <span className="text-slate-400">· {m.hours}h</span>
                                    <ChevronDown className={`h-3 w-3 text-slate-400 transition-transform ${mOpen ? "rotate-180" : ""}`} />
                                  </span>
                                  <span className="font-bold text-slate-900">{money(m.payWithAdj)}</span>
                                </button>
                                {mOpen && (
                                  <div className="space-y-1 border-t border-line/70 px-2 py-1.5" data-testid={`breakdown-crew-mover-jobs-${g.key}-${i}`}>
                                    {m.jobs.map((jb) => (
                                      <div key={jb.job_id} className="flex items-center justify-between gap-2 text-[11px] text-slate-500" data-testid={`breakdown-crew-mover-job-${g.key}-${i}-${jb.job_id}`}>
                                        <span className="flex min-w-0 items-center gap-1 truncate">
                                          <span className="truncate">{jb.job_id} · {jb.date}</span>
                                          <span className={`shrink-0 rounded-full px-1 py-0.5 text-[8px] font-bold uppercase ${jb.source === "movers_buddy" ? "bg-amber-100 text-amber-700" : "bg-blaze/10 text-blaze"}`}>
                                            {jb.source === "movers_buddy" ? "Buddy" : "MN"}
                                          </span>
                                        </span>
                                        <span className="shrink-0 font-semibold text-slate-700">{jb.hours}h · {money(jb.pay)}</span>
                                      </div>
                                    ))}
                                    {m.adjNet !== 0 && (
                                      <div className="flex items-center justify-between gap-2 border-t border-line/70 pt-1 text-[11px]" data-testid={`breakdown-crew-adj-${g.key}-${i}`}>
                                        <span className="text-slate-500">Adjustments</span>
                                        <span className={`font-bold ${m.adjNet >= 0 ? "text-green-700" : "text-red-600"}`}>{m.adjNet >= 0 ? "+" : "−"}{money(Math.abs(m.adjNet))}</span>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                          <div className="flex items-center justify-between border-t border-line pt-1 text-xs font-bold text-slate-900">
                            <span>Total crew pay</span>
                            <span>{money(g.crew)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-3" data-testid="earnings-movers-pay">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex gap-1 rounded-xl border border-line bg-white p-1">
              {[["week", "Weekly"], ["day", "Daily"]].map(([g, label]) => (
                <button
                  key={g}
                  onClick={() => setMoverGrain(g)}
                  className={`rounded-lg px-4 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${moverGrain === g ? "bg-blaze text-white" : "text-slate-500 hover:text-slate-700"}`}
                  data-testid={`movers-pay-grain-${g}`}
                >
                  {label}
                </button>
              ))}
            </div>
            {moverGroups.length > 1 && (
              <button
                type="button"
                onClick={() => markEarlierWeeksPaid(moverGroups)}
                disabled={bulking}
                className="rounded-xl border border-green-600 bg-green-50 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-green-700 transition-colors hover:bg-green-600 hover:text-white disabled:opacity-50"
                data-testid="movers-pay-mark-earlier"
              >
                {bulking ? "Marking…" : "Mark earlier paid"}
              </button>
            )}
          </div>
          {moverGroups.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-line bg-slate-50/50 py-16 text-center text-sm text-slate-500" data-testid="movers-pay-empty">
              No mover pay recorded yet.
            </div>
          ) : (
            moverGroups.map((w) => (
              <div key={w.key} className="rounded-2xl border border-line bg-white p-4 shadow-sm" data-testid={`movers-pay-week-${w.key}`}>
                <div className="flex items-center justify-between">
                  <p className="text-sm font-bold text-slate-900">{moverGrain === "week" ? weekLabel(w.key) : dayLabel(w.key)}</p>
                  <div className="text-right">
                    <p className="text-[9px] font-bold uppercase tracking-widest text-slate-500">{moverGrain === "week" ? "Week" : "Day"} total</p>
                    <p className="text-base font-black text-slate-900" data-testid={`movers-pay-total-${w.key}`}>{money(w.total)}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => markWeekPaid(w.weekKey, w.movers, !w.allPaid)}
                  className={`mt-2 w-full rounded-lg px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-white transition-colors ${w.allPaid ? "bg-slate-500 hover:bg-slate-600" : "bg-green-600 hover:bg-green-700"}`}
                  data-testid={`movers-pay-week-mark-${w.key}`}
                >
                  {w.allPaid ? "Mark all unpaid" : "Mark all paid"}
                </button>
                <div className="mt-3 space-y-1.5">
                  {w.movers.map((m, i) => {
                    const mk = `${w.key}|${m.mover}`;
                    const expanded = !!openMover[mk];
                    return (
                      <div key={i} className="overflow-hidden rounded-xl bg-slate-50" data-testid={`movers-pay-mover-${w.key}-${i}`}>
                        <button
                          type="button"
                          onClick={() => setOpenMover((p) => ({ ...p, [mk]: !p[mk] }))}
                          className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm"
                          data-testid={`movers-pay-mover-toggle-${w.key}-${i}`}
                        >
                          <span className="flex min-w-0 flex-col font-semibold text-slate-800">
                            <span className="flex min-w-0 items-center gap-2">
                              <HardHat className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                              <span className="truncate">{m.mover}</span>
                              <span
                                className={`shrink-0 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider ${m.paid ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}
                                data-testid={`movers-pay-status-${w.key}-${i}`}
                              >
                                {m.paid ? "Paid" : "Unpaid"}
                              </span>
                            </span>
                            {m.extrasEarned ? (
                              <span className={`ml-5 mt-0.5 text-[10px] font-bold ${m.extrasEarned >= 0 ? "text-green-700" : "text-red-600"}`} data-testid={`movers-pay-extras-earned-${w.key}-${i}`}>
                                Extras & bonuses {m.extrasEarned >= 0 ? "+" : "−"}{money(Math.abs(m.extrasEarned))}
                              </span>
                            ) : null}
                          </span>
                          <span className="flex shrink-0 items-center gap-2 text-slate-600">
                            <span className="text-xs text-slate-400">{m.hours}h</span>
                            <span className="font-black text-slate-900" data-testid={`movers-pay-amt-${w.key}-${i}`}>{money(m.payWithAdj)}</span>
                            <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${expanded ? "rotate-180" : ""}`} />
                          </span>
                        </button>
                        {expanded && (
                          <div className="border-t border-line bg-white px-3 py-2.5" data-testid={`movers-pay-detail-${w.key}-${i}`}>
                            <p className="mb-1.5 text-[9px] font-bold uppercase tracking-widest text-slate-400">Jobs in this {moverGrain === "week" ? "week" : "day"}</p>
                            <div className="space-y-1.5">
                              {m.jobs.map((jb) => (
                                <div key={jb.job_id} className="rounded-lg bg-slate-50 px-2.5 py-1.5" data-testid={`movers-pay-job-${w.key}-${i}-${jb.job_id}`}>
                                  <div className="flex items-center justify-between text-xs">
                                    <span className="flex min-w-0 items-center gap-1.5 truncate font-medium text-slate-700">
                                      <span className="truncate">{jb.job_id} <span className="text-slate-400">· {jb.date}</span></span>
                                      <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-bold uppercase ${jb.source === "movers_buddy" ? "bg-amber-100 text-amber-700" : "bg-blaze/10 text-blaze"}`}>
                                        {jb.source === "movers_buddy" ? "Buddy" : "MN"}
                                      </span>
                                    </span>
                                    <span className="ml-2 shrink-0 text-slate-500">{jb.hours}{jb.travel ? ` + ${jb.travel}` : ""}h · {money(jb.pay)}</span>
                                  </div>
                                  {jb.extra ? (
                                    <p className="mt-0.5 text-[10px] font-semibold text-green-700" data-testid={`movers-pay-extra-${w.key}-${i}-${jb.job_id}`}>+{money(jb.extra)} heavy lifting / stairs{jb.extraNote ? ` · ${jb.extraNote}` : ""}</p>
                                  ) : null}
                                  <p className="mt-0.5 text-[10px] text-slate-400">Company paid you (your share): <span className="font-semibold text-blaze">{money(jb.jobShare)}</span></p>
                                </div>
                              ))}
                            </div>
                            {moverGrain === "week" && (
                              <>
                                {m.adjNet !== 0 && (
                                  <div className="mt-1.5 flex items-center justify-between text-[11px] text-slate-500" data-testid={`movers-pay-adjline-${w.key}-${i}`}>
                                    <span>Base pay {money(m.pay)} {m.adjNet >= 0 ? "+" : "−"} {money(Math.abs(m.adjNet))}</span>
                                    <span className="font-bold text-slate-900">= {money(m.payWithAdj)}</span>
                                  </div>
                                )}
                                <PayoutAdjustments
                                  payeeType="mover"
                                  payeeName={m.mover}
                                  weekStart={w.weekKey}
                                  jobs={m.jobs.map((jb) => ({ job_id: jb.job_id, date: jb.date }))}
                                  items={m.adj}
                                  onChange={load}
                                  idPrefix={`madj-${w.key}-${i}`}
                                />
                              </>
                            )}
                            <button
                              type="button"
                              onClick={() => markMoverPaid(w.weekKey, m.mover, !m.paid)}
                              className={`mt-2.5 w-full rounded-lg px-3 py-2 text-[10px] font-bold uppercase tracking-widest text-white transition-colors ${m.paid ? "bg-slate-500 hover:bg-slate-600" : "bg-green-600 hover:bg-green-700"}`}
                              data-testid={`movers-pay-mark-${w.key}-${i}`}
                            >
                              {m.paid ? "Mark Unpaid" : "Mark Paid"}
                            </button>
                            {m.paid && m.paid_at ? (
                              <p className="mt-1 text-center text-[10px] text-slate-400">Paid {m.paid_at}</p>
                            ) : null}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>
      )}

      <p className="flex items-center justify-center gap-1.5 text-center text-[11px] text-slate-400">
        <Hand className="h-3 w-3" /> Earnings are shown only after a job is completed.
      </p>
    </div>
  );
};
